import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  type EntityState,
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { EWidgetRegion, EWidgetSection } from '#shared';

import {
  generateWidgetId,
  type WidgetId,
  type WidgetType,
} from '../widgetManager';

import { blockSlice } from './block.slice';

import type { RootState } from '../redux-store';
import type { CssSize } from './region.slice';

export interface SectionStateItem {
  id: EWidgetSection;
  region: EWidgetRegion;
  disabled?: boolean;
  direction: 'row' | 'column';
  blockOrder: WidgetId[];
  blockSizes: Record<WidgetId, CssSize>;
  sealed?: boolean;
}
export type SectionsState = EntityState<SectionStateItem, EWidgetSection>;

const adapter = createEntityAdapter<SectionStateItem, EWidgetSection>({
  selectId: s => s.id,
});

// Helper function to remove block from section
const removeBlockFromSection = (
  section: SectionStateItem,
  blockId: WidgetId,
): void => {
  section.blockOrder = section.blockOrder.filter(id => id !== blockId);

  // avoid dynamic delete

  const { [blockId]: _, ...rest } = section.blockSizes;
  section.blockSizes = rest;
};

// type UpsertSectionPayload = Section;
type SetSectionBlocksPayload = {
  sectionId: EWidgetSection;
  blockIds: WidgetId[];
};
type AddBlockPayload = {
  sectionId: EWidgetSection;
  blockId: WidgetId;
  size: CssSize;
};
type RemoveBlockPayload = { sectionId: EWidgetSection; blockId: WidgetId };
type MoveBlockPayload = {
  sectionFromId: EWidgetSection;
  sectionToId: EWidgetSection;
  blockId: WidgetId;
};
type SetBlockSizePayload = {
  sectionId: EWidgetSection;
  blockId: WidgetId;
  size: CssSize;
};
type SetBlockSizesPayload = {
  sectionId: EWidgetSection;
  sizes: Record<WidgetId, CssSize>;
};
type SetDirectionPayload = {
  sectionId: EWidgetSection;
  direction: 'row' | 'column';
};
type ToggleSectionPayload = EWidgetSection;
type EnableSectionPayload = EWidgetSection;
type DisableSectionPayload = EWidgetSection;

export const sectionSlice = createSlice({
  name: 'layout/sections',
  initialState: adapter.getInitialState(),
  reducers: {
    sectionRemove: adapter.removeOne,

    sectionSetBlocks(
      state,
      { payload }: PayloadAction<SetSectionBlocksPayload>,
    ) {
      const section = state.entities[payload.sectionId];
      if (!section) {
        return;
      }
      adapter.updateOne(state, {
        id: payload.sectionId,
        changes: {
          blockOrder: payload.blockIds,
          blockSizes: payload.blockIds.reduce<Record<string, CssSize>>(
            (acc, id) => {
              acc[id] = section.blockSizes[id] || '1fr';
              return acc;
            },
            {},
          ),
        },
      });

      const blockIds = payload.blockIds.map<[string, CssSize]>(id => [
        id,
        '1fr',
      ]);
      section.blockSizes = Object.fromEntries(blockIds) as Record<
        string,
        CssSize
      >;

      // Clean up any widget instances that are no longer in this section
      Object.values(state.entities).forEach(s => {
        if (s?.id === payload.sectionId) {
          return;
        }
        s?.blockOrder.forEach(blockId => {
          if (payload.blockIds.includes(blockId)) {
            removeBlockFromSection(s, blockId);
          }
        });
      });
    },

    sectionMoveBlock(state, { payload }: PayloadAction<MoveBlockPayload>) {
      const fromSection = state.entities[payload.sectionFromId];
      const toSection = state.entities[payload.sectionToId];
      if (!fromSection || !toSection) {
        return;
      }

      // Remove block from the original section
      fromSection.blockOrder = fromSection.blockOrder.filter(
        id => id !== payload.blockId,
      );

      // Add block to the new section
      toSection.blockOrder.push(payload.blockId);
    },

    sectionAddBlock: {
      prepare(
        sectionId: EWidgetSection,
        widgetType: WidgetType,
        blockId?: WidgetId,
        size: CssSize = '1fr',
      ) {
        const payload: AddBlockPayload = {
          sectionId,
          blockId: blockId ?? generateWidgetId(widgetType),
          size,
        };
        return { payload };
      },
      reducer(state, { payload }: PayloadAction<AddBlockPayload>) {
        const s = state.entities[payload.sectionId];
        if (!s) {
          return;
        }

        s.blockOrder.push(payload.blockId);
        s.blockSizes[payload.blockId] = payload.size;
      },
    },

    /**
     * Add a block at a specific position in the section
     * If position is undefined, appends to the end
     */
    sectionAddBlockAtPosition(
      state,
      {
        payload,
      }: PayloadAction<{
        sectionId: EWidgetSection;
        widgetType: WidgetType;
        blockId: WidgetId;
        position?: number;
        size?: CssSize;
      }>,
    ) {
      const s = state.entities[payload.sectionId];
      if (!s) {
        return;
      }

      const size = payload.size ?? '1fr';

      if (payload.position === undefined) {
        // Append to end
        s.blockOrder.push(payload.blockId);
      } else {
        // Insert at specific position
        const insertPos = Math.max(
          0,
          Math.min(payload.position, s.blockOrder.length),
        );
        s.blockOrder.splice(insertPos, 0, payload.blockId);
      }

      s.blockSizes[payload.blockId] = size;
    },

    sectionRemoveBlock(state, { payload }: PayloadAction<RemoveBlockPayload>) {
      const s = state.entities[payload.sectionId];
      if (!s) {
        return;
      }

      removeBlockFromSection(s, payload.blockId);
    },

    sectionSetBlockSize(
      state,
      { payload }: PayloadAction<SetBlockSizePayload>,
    ) {
      const s = state.entities[payload.sectionId];
      if (!s) {
        return;
      }
      s.blockSizes[payload.blockId] = payload.size;
    },

    sectionSetBlockSizes(
      state,
      { payload }: PayloadAction<SetBlockSizesPayload>,
    ) {
      const s = state.entities[payload.sectionId];
      if (!s) {
        return;
      }
      Object.assign(s.blockSizes, payload.sizes);
    },

    sectionSetDirection(
      state,
      { payload }: PayloadAction<SetDirectionPayload>,
    ) {
      const s = state.entities[payload.sectionId];
      if (!s) {
        return;
      }
      s.direction = payload.direction;
    },
    toggleSection(
      state,
      { payload: sectionId }: PayloadAction<ToggleSectionPayload>,
    ) {
      const s = state.entities[sectionId];
      if (!s) {
        return;
      }
      adapter.updateOne(state, {
        id: sectionId,
        changes: { disabled: !s.disabled },
      });
    },
    enableSection(
      state,
      { payload: sectionId }: PayloadAction<EnableSectionPayload>,
    ) {
      const s = state.entities[sectionId];
      if (!s) {
        return;
      }
      adapter.updateOne(state, { id: sectionId, changes: { disabled: false } });
    },
    disableSection(
      state,
      { payload: sectionId }: PayloadAction<DisableSectionPayload>,
    ) {
      const s = state.entities[sectionId];
      if (!s) {
        return;
      }
      adapter.updateOne(state, { id: sectionId, changes: { disabled: true } });
    },

    /**
     * Replace entire sections state (used for template application)
     */
    setSections(state, action: PayloadAction<SectionStateItem[]>) {
      adapter.setAll(state, action.payload);
    },

    /**
     * Remove a section only if it's empty (has no blocks)
     * Used for cleanup after widget removal
     */
    sectionRemoveIfEmpty(
      state,
      { payload: sectionId }: PayloadAction<EWidgetSection>,
    ) {
      const section = state.entities[sectionId];
      if (!section) {
        return;
      }

      // Only remove if the section is empty
      if (section.blockOrder.length === 0) {
        adapter.removeOne(state, sectionId);
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(blockSlice.actions.blockRemove, (state, action) => {
      // Find and remove the widget instances associated with the block
      const blockId = action.payload;

      // Remove the block from all sections that contain it
      Object.values(state.entities).forEach(section => {
        if (section?.blockOrder.includes(blockId)) {
          removeBlockFromSection(section, blockId);
        }
      });
    });
  },
});

const selectors = adapter.getSelectors<RootState>(s => s.sections);

export const sectionSelectors = {
  useSelectSectionById: (id: EWidgetSection) =>
    useSelector((state: RootState) => selectors.selectById(state, id)),
  useSelectSectionIds: () =>
    useSelector((state: RootState) => selectors.selectIds(state)),
  useSelectSectionEntities: () =>
    useSelector((state: RootState) => selectors.selectEntities(state)),
  useSelectSectionAll: () =>
    useSelector((state: RootState) => selectors.selectAll(state)),
  useSelectSectionTotal: () =>
    useSelector((state: RootState) => selectors.selectTotal(state)),
  useSelectSectionBlockIds: (id: EWidgetSection) =>
    useSelector(
      (state: RootState) => state.sections.entities[id]?.blockOrder ?? [],
    ),
  useSelectSectionBlockSizes: (id: EWidgetSection) =>
    useSelector(
      (state: RootState) => state.sections.entities[id]?.blockSizes ?? {},
    ),
  useSelectSectionEnabled: (id: EWidgetSection) =>
    useSelector((state: RootState) => !state.sections.entities[id].disabled),
  useSelectSectionTemplate: (id: EWidgetSection) =>
    useSelector((state: RootState) => {
      const sec = state.sections.entities[id];
      if (!sec) {
        return '';
      }
      return sec.blockOrder.map(b => sec.blockSizes[b] ?? '1fr').join(' ');
    }),
};

export const sectionReducer = sectionSlice.reducer;
export const sectionActions = sectionSlice.actions;
